import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { mbaAPI, projectsAPI } from '../utils/api';
import { GENERIC_PREVIEW_HTML_TEMPLATE, PREMIUM_HTML_TEMPLATE } from './ProjectDetail_Premium';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:8000';

const normalizeUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `${API_BASE_URL}${url}`;
  if (url.startsWith('uploads/')) return `${API_BASE_URL}/${url}`;
  if (url.startsWith('/api/')) return `${API_BASE_URL}${url}`;
  if (url.startsWith('api/')) return `${API_BASE_URL}/${url}`;
  return url;
};

export default function PremiumPreviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Core states
  const [html, setHtml] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);

  // Enquiry modal & success states
  const [showEnquiryModal, setShowEnquiryModal] = useState(searchParams.get('enquiry') === 'true');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  // Form fields state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: 'I am interested in this project. Please share complete details and availability.'
  });

  const [formErrors, setFormErrors] = useState({});

  const source = useMemo(() => searchParams.get('source') || 'project', [searchParams]);

  // Inject Tailwind CSS dynamically for this page's React widgets (header, modal)
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Listen for actions from the iframe (e.g. click Buy button, click Download files)
  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event.data && (event.data.type === 'BUY_NOW' || event.data.type === 'OPEN_ENQUIRY_MODAL')) {
        setShowEnquiryModal(true);
      }
    };
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, []);

  // Fetch project details on mount
  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, source]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      setError('');
      let data = null;

      if (source === 'mba') {
        try {
          const resp = await mbaAPI.getProjectById(id);
          data = resp?.data;
        } catch (err) {
          const res = await fetch(`${API_BASE_URL}/api/mba-projects/${id}`, { credentials: 'omit' });
          if (!res.ok) throw new Error(`MBA project fetch failed: ${res.status}`);
          data = await res.json();
        }
      } else {
        try {
          const publicResp = await fetch(`${API_BASE_URL}/api/projects/public`, { credentials: 'omit' });
          if (publicResp.ok) {
            const publicJson = await publicResp.json();
            const publicProject = Array.isArray(publicJson?.data)
              ? publicJson.data.find((item) => String(item.id) === String(id))
              : null;
            if (publicProject) data = publicProject;
          }
        } catch (err) {
          // Fall back below
        }

        if (!data) {
          try {
            const resp = await projectsAPI.getById(id);
            data = resp?.data;
          } catch (err) {
            const res = await fetch(`${API_BASE_URL}/api/projects/${id}`, { credentials: 'omit' });
            if (!res.ok) throw new Error(`Project fetch failed: ${res.status}`);
            data = await res.json();
          }
        }
      }

      if (!data) throw new Error('Project not found');

      let parsedImages = [];
      try {
        parsedImages = data?.images
          ? (typeof data.images === 'string' ? JSON.parse(data.images) : data.images)
          : [];
      } catch (e) {
        parsedImages = [];
      }

      const normalizedProject = {
        ...data,
        images: Array.isArray(parsedImages) ? parsedImages.map(normalizeUrl) : [],
        pdf_file: normalizeUrl(data.pdf_file),
        doc_file: normalizeUrl(data.doc_file),
        report_file: normalizeUrl(data.report_file),
        zip_file: normalizeUrl(data.zip_file),
        demo_video_url: normalizeUrl(data.demo_video_url),
        installation_video_url: normalizeUrl(data.installation_video_url),
      };

      const template = source === 'mba' ? PREMIUM_HTML_TEMPLATE : GENERIC_PREVIEW_HTML_TEMPLATE;

      const previewHtml = template({
        title: normalizedProject.title || normalizedProject.name || 'Project',
        specialization: normalizedProject.specialization || normalizedProject.category || '',
        project_type: normalizedProject.type || normalizedProject.project_type || 'Project',
        original_price: normalizedProject.price != null && normalizedProject.price !== ''
          ? `₹${Number(normalizedProject.price).toLocaleString()}`
          : (normalizedProject.price_original ? `₹${Number(normalizedProject.price_original).toLocaleString()}` : '₹4999'),
        discount_price: normalizedProject.discount_price != null && normalizedProject.discount_price !== ''
          ? `₹${Number(normalizedProject.discount_price).toLocaleString()}`
          : (normalizedProject.price ? `₹${Number(normalizedProject.price).toLocaleString()}` : (normalizedProject.price_formatted || '₹2999')),
        author: normalizedProject.author || normalizedProject.author_name || normalizedProject.owner || normalizedProject.contact_name || '',
        project: normalizedProject,
      });

      setProject(normalizedProject);
      setHtml(previewHtml);
    } catch (err) {
      console.error('PremiumPreviewPage fetch/render failed', err);
      setError('Unable to load premium preview.');
    } finally {
      setLoading(false);
    }
  };

  // Navigate back and restore scroll position via browser history
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(source === 'mba' ? '/mba-projects' : '/projects');
    }
  };

  // Form input validation
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Full Name is required";
    }
    if (!formData.email.trim()) {
      errors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    const digitsOnly = formData.mobile.replace(/\D/g, '');
    if (!formData.mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (digitsOnly.length !== 10) {
      errors.mobile = "Mobile number must be exactly 10 digits";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    if (formErrors[id]) {
      setFormErrors(prev => ({ ...prev, [id]: '' }));
    }
  };

  // Submit handler
  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setApiError('');

      const response = await fetch(`${API_BASE_URL}/api/enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: String(id),
          projectTitle: project.title || project.name || 'MBA Project',
          name: formData.name.trim(),
          mobile: formData.mobile.trim(),
          email: formData.email.trim(),
          college: null,
          university: null,
          specialization: project.specialization || project.category || (source === 'mba' ? 'MBA - Project' : 'Software - Project'),
          message: formData.message.trim() || null,
          source: source
        })
      });

      const resJson = await response.json();
      if (response.ok && resJson.success) {
        setSubmitSuccess(true);
        // Reset form data
        setFormData({
          name: '',
          email: '',
          mobile: '',
          message: 'I am interested in this project. Please share complete details and availability.'
        });
      } else {
        throw new Error(resJson.detail || 'Failed to submit enquiry.');
      }
    } catch (err) {
      console.error('Enquiry submit error:', err);
      setApiError(err.message || 'Failed to submit enquiry. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetFormState = () => {
    setSubmitSuccess(false);
    setApiError('');
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 font-sans">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-500 font-semibold">Loading premium project preview...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 font-sans text-center">
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-6 max-w-md shadow-sm">
          <h2 className="text-xl font-bold mb-2">Preview Loading Failed</h2>
          <p className="text-sm text-red-500 mb-4">{error || 'Unable to load project metadata.'}</p>
          <button 
            onClick={fetchProject} 
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm transition-all"
          >
            🔄 Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50 font-sans">
      
      {/* Sticky SPA Back Navigation Header */}
      <header className="flex items-center px-4 py-3 bg-white border-b border-gray-200 z-10 h-14 shadow-sm flex-shrink-0">
        <button 
          onClick={handleBack} 
          className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 hover:text-black border border-gray-300 hover:border-gray-400 px-4 py-1.5 rounded-full font-bold text-xs md:text-sm transition-all shadow-sm active:scale-[0.98]"
        >
          ← Back to {source === 'mba' ? 'MBA Projects' : 'Software Projects'}
        </button>
      </header>

      {/* Stands for Preview Frame */}
      <div className="flex-1 relative">
        <iframe
          title="Premium Preview"
          srcDoc={html}
          className="w-full h-full border-none block bg-slate-50"
        />
      </div>

      {/* React Enquiry Modal Overlay */}
      {showEnquiryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity duration-300">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] transform scale-100 transition-all duration-300 animate-fadeIn">
            
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Request Project Details</h3>
                <p className="text-xs text-gray-500 mt-0.5">Please provide your details below to receive files & report guidance.</p>
              </div>
              {!submitting && (
                <button 
                  onClick={() => { setShowEnquiryModal(false); resetFormState(); }} 
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none p-1 transition-colors"
                >
                  &times;
                </button>
              )}
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {submitSuccess ? (
                /* Success Screen */
                <div className="text-center py-6 animate-fadeIn">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <span className="text-3xl">✓</span>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Enquiry Submitted Successfully</h4>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed px-2">
                    Thank you for contacting us. We have received your request. A confirmation email has been sent to you. Our team will contact you shortly.
                  </p>
                  <div className="flex flex-col gap-3 max-w-xs mx-auto">
                    <button 
                      onClick={() => { setShowEnquiryModal(false); handleBack(); }}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold text-sm rounded-xl transition-all shadow-md active:scale-[0.98]"
                    >
                      ← Back To Projects
                    </button>
                    <button 
                      onClick={resetFormState}
                      className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm rounded-xl transition-all"
                    >
                      Submit Another Enquiry
                    </button>
                  </div>
                </div>
              ) : (
                /* Enquiry Input Form */
                <form onSubmit={handleSubmitEnquiry} className="space-y-4">
                  {apiError && (
                    <div className="bg-red-50 border border-red-200 text-red-600 text-xs font-semibold rounded-lg p-3">
                      ⚠️ {apiError}
                    </div>
                  )}

                  {/* Pre-filled Project Title */}
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Project Title</label>
                    <input 
                      type="text" 
                      value={project.title || project.name} 
                      disabled 
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 font-semibold text-sm outline-none cursor-not-allowed" 
                    />
                  </div>



                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-blue-100 outline-none transition-all ${
                        formErrors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
                      }`}
                      placeholder="Enter your full name" 
                    />
                    {formErrors.name && <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.name}</p>}
                  </div>

                  {/* Email & Mobile Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Mobile */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                        Mobile Number <span className="text-red-500">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="mobile" 
                        value={formData.mobile}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-blue-100 outline-none transition-all ${
                          formErrors.mobile ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
                        }`}
                        placeholder="10-digit number" 
                      />
                      {formErrors.mobile && <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.mobile}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input 
                        type="email" 
                        id="email" 
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-blue-100 outline-none transition-all ${
                          formErrors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
                        }`}
                        placeholder="Enter email address" 
                      />
                      {formErrors.email && <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.email}</p>}
                    </div>
                  </div>



                  {/* Message */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Your Message</label>
                    <textarea 
                      id="message" 
                      rows="3" 
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-slate-800 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
                    />
                  </div>

                  {/* Submit Actions */}
                  <div className="flex gap-3 pt-2">
                    <button 
                      type="submit" 
                      disabled={submitting}
                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold text-sm shadow-md hover:shadow-blue-100 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <span>Submit Enquiry</span>
                      )}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => { setShowEnquiryModal(false); resetFormState(); }} 
                      disabled={submitting}
                      className="flex-1 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm transition-all disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
